import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Container, Typography, CircularProgress, Alert, Grid, Snackbar, IconButton, Card, CardContent, Chip, FormControl, InputLabel, MenuItem, Select, Stack } from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import axios from 'axios';
import TemplateCard from '../../components/Homepage/TemplateCard';
import TemplateDetailDialog from '../../components/Homepage/TemplateDetailDialog';
import GuidedTemplateWizard from '../../components/Homepage/GuidedTemplateWizard';
import GuidedFlowCard from '../../components/Homepage/GuidedFlowCard';
import AdminSupportCard from '../../components/Homepage/AdminSupportCard';
import { getTemplates, getTemplateById } from '../../services/templateService';
import url from '../../url';
import './Homepage.css';

export default function Homepage({
  onActivityCreated,
  onViewChange,
  showExploreActivities = false,
  exploreLoading = false,
  exploreError = '',
  exploreSortBy = 'latest',
  onExploreSortChange,
  sortedExploreActivities = [],
  onExploreActivityClick,
}) {
  const { t, i18n } = useTranslation('homepage');
  const [templates, setTemplates] = useState([]);
  const [guidedFlows, setGuidedFlows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [selectedFlow, setSelectedFlow] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const scrollContainerRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const templatesList = await getTemplates('activity-template', i18n.language);
      console.log('Fetched activity templates:', templatesList);
      setTemplates(templatesList);
      setError(null);
    } catch (err) {
      setError(err.message || t('quickStart.loadError'));
      console.error('Error fetching templates:', err);
    } finally {
      setLoading(false);
    }
  }, [i18n.language, t]);

  const fetchGuidedFlows = useCallback(async () => {
    try {
      const flowsList = await getTemplates('guided-flow', i18n.language);
      console.log('Fetched guided flows:', flowsList);
      setGuidedFlows(flowsList);
    } catch (err) {
      console.error('Error fetching guided flows:', err);
      // 不影響主要功能，只記錄錯誤
    }
  }, [i18n.language]);

  useEffect(() => {
    fetchTemplates();
    fetchGuidedFlows();
  }, [fetchTemplates, fetchGuidedFlows]);

  useEffect(() => {
    checkScrollButtons();
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', checkScrollButtons);
      window.addEventListener('resize', checkScrollButtons);
      return () => {
        container.removeEventListener('scroll', checkScrollButtons);
        window.removeEventListener('resize', checkScrollButtons);
      };
    }
  }, [templates]);

  const checkScrollButtons = () => {
    const container = scrollContainerRef.current;
    if (container) {
      setShowLeftArrow(container.scrollLeft > 0);
      setShowRightArrow(
        container.scrollLeft < container.scrollWidth - container.clientWidth - 10
      );
    }
  };

  const scroll = (direction) => {
    const container = scrollContainerRef.current;
    if (container) {
      const scrollAmount = 300;
      container.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const handleTemplateClick = async (template) => {
    try {
      // 獲取完整模板詳情
      const fullTemplate = await getTemplateById(template.id, i18n.language);
      setSelectedTemplate(fullTemplate);
      setDialogOpen(true);
    } catch (err) {
      setError(err.message || t('quickStart.detailLoadError'));
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedTemplate(null);
  };

  const handleAdminSupportClick = async () => {
    const userId = localStorage.getItem('userId');
    const jwtToken = localStorage.getItem('jwtToken');

    if (!userId || !jwtToken) {
      setSnackbar({
        open: true,
        message: t('adminSupport.loginRequired'),
        severity: 'warning'
      });
      return;
    }

    try {
      const adminJoinCode = '4949332';
      const headers = { Authorization: `Bearer ${jwtToken}` };
      const payload = { userId };

      // 加入站長回報空間
      await axios.put(
        `${url.backendHost}api/groups/${adminJoinCode}/join`,
        payload,
        { headers, withCredentials: true }
      );

      // 設置 joinCode 並導航到主頁面
      localStorage.setItem('joinCode', adminJoinCode);
      
      setSnackbar({
        open: true,
        message: t('adminSupport.entering'),
        severity: 'success'
      });

      // 延遲一下讓使用者看到成功訊息，然後導航
      setTimeout(() => {
        if (onViewChange) {
          onViewChange('main');
        }
      }, 1000);

    } catch (err) {
      if (err.response && err.response.status === 409) {
        // 已經加入過，直接導航
        localStorage.setItem('joinCode', '4949332');
        setSnackbar({
          open: true,
          message: t('adminSupport.entering'),
          severity: 'success'
        });
        setTimeout(() => {
          if (onViewChange) {
            onViewChange('main');
          }
        }, 1000);
      } else {
        setSnackbar({
          open: true,
          message: t('adminSupport.enterFailed'),
          severity: 'error'
        });
        console.error('Error joining admin support:', err);
      }
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleOpenWizard = () => {
    setWizardOpen(true);
  };

  const handleCloseWizard = () => {
    setWizardOpen(false);
    setSelectedFlow(null);
  };

  const handleFlowCardClick = async (flow) => {
    try {
      // 獲取完整的引導流程配置
      const fullFlow = await getTemplateById(flow.id, i18n.language);
      setSelectedFlow(fullFlow);
      setWizardOpen(true);
    } catch (err) {
      setError(err.message || t('guidedFlow.loadError'));
    }
  };

  return (
    <Box className="homepage-container" sx={{ bgcolor: '#f5f7fa', minHeight: '100vh', pb: 4 }}>

      
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ mb: 6 }}>
          <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
          {t('title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('subtitle')}
          </Typography>
        </Box>




        {/* 快速開始區塊 */}
        <Box sx={{ mb: 6 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <Box>
              <Typography variant="h5" component="h2" fontWeight="bold" gutterBottom>
                {t('quickStart.title')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('quickStart.subtitle')}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {showLeftArrow && (
                <IconButton 
                  onClick={() => scroll('left')}
                  sx={{ 
                    bgcolor: 'white', 
                    boxShadow: 1,
                    '&:hover': { bgcolor: 'grey.100' }
                  }}
                >
                  <ArrowBackIosNewIcon />
                </IconButton>
              )}
              {showRightArrow && (
                <IconButton 
                  onClick={() => scroll('right')}
                  sx={{ 
                    bgcolor: 'white', 
                    boxShadow: 1,
                    '&:hover': { bgcolor: 'grey.100' }
                  }}
                >
                  <ArrowForwardIosIcon />
                </IconButton>
              )}
            </Box>
          </Box>

          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 4 }}>
              {error}
            </Alert>
          )}

          {!loading && !error && templates.length === 0 && (
            <Alert severity="info" sx={{ mb: 4 }}>
              {t('quickStart.noTemplates')}
            </Alert>
          )}

          {!loading && templates.length > 0 && (
            <Box 
              ref={scrollContainerRef}
              sx={{ 
                display: 'flex', 
                gap: 3, 
                overflowX: 'auto',
                pb: 2,
                scrollbarWidth: 'thin',
                '&::-webkit-scrollbar': {
                  height: 8,
                },
                '&::-webkit-scrollbar-track': {
                  bgcolor: 'grey.200',
                  borderRadius: 4,
                },
                '&::-webkit-scrollbar-thumb': {
                  bgcolor: 'grey.400',
                  borderRadius: 4,
                  '&:hover': {
                    bgcolor: 'grey.500',
                  },
                }
              }}
            >
              {templates.map((template) => (
                <Box 
                  key={template.id} 
                  sx={{ 
                    minWidth: '280px', 
                    maxWidth: '280px',
                    flexShrink: 0 
                  }}
                >
                  <TemplateCard
                    template={template}
                    onClick={() => handleTemplateClick(template)}
                  />
                </Box>
              ))}
            </Box>
          )}
        </Box>

        {/* 引導流程區塊 - 只在有引導流程時顯示 */}
        {guidedFlows.length > 0 && (
          <Box sx={{ mb: 6 }}>
            <Box>
              <Typography variant="h5" component="h2" fontWeight="bold" gutterBottom>
                {t('guidedFlow.title')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('guidedFlow.subtitle')}
              </Typography>
            </Box>
            <Box 
              sx={{ 
                display: 'flex', 
                gap: 3, 
                overflowX: 'auto',
                pb: 2,
                scrollbarWidth: 'thin',
                '&::-webkit-scrollbar': {
                  height: 8,
                },
                '&::-webkit-scrollbar-track': {
                  bgcolor: 'grey.200',
                  borderRadius: 4,
                },
                '&::-webkit-scrollbar-thumb': {
                  bgcolor: 'grey.400',
                  borderRadius: 4,
                  '&:hover': {
                    bgcolor: 'grey.500',
                  },
                }
              }}
            >
              {guidedFlows.map((flow) => (
                <Box 
                  key={flow.id} 
                  sx={{ 
                    minWidth: '15vw', 
                    maxWidth: '50vw',
                    flexShrink: 0 
                  }}
                >
                  <GuidedFlowCard
                    title={flow.name}
                    description={flow.description}
                    badge={flow.badge}
                    gradient={flow.gradient}
                    onClick={() => handleFlowCardClick(flow)}
                    
                  />
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {showExploreActivities && (
        <Box sx={{ mb: 6 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 3, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="h5" component="h2" fontWeight="bold" gutterBottom>
                {t('exploreActivities.title')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('exploreActivities.subtitle')}
              </Typography>
            </Box>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel id="explore-sort-label">{t('exploreActivities.sort')}</InputLabel>
              <Select
                labelId="explore-sort-label"
                label={t('exploreActivities.sort')}
                value={exploreSortBy}
                onChange={onExploreSortChange}
              >
                <MenuItem value="latest">{t('exploreActivities.latest')}</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {exploreLoading && (
            <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
              <Typography color="text.secondary">{t('exploreActivities.loading')}</Typography>
            </Box>
          )}

          {!exploreLoading && exploreError && (
            <Box sx={{ mb: 2 }}>
              <Typography color="error">{exploreError}</Typography>
            </Box>
          )}

          {!exploreLoading && !exploreError && sortedExploreActivities.length === 0 && (
            <Box sx={{ py: 8, textAlign: 'center', color: 'text.secondary' }}>
              {t('exploreActivities.empty')}
            </Box>
          )}

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 2 }}>
            {sortedExploreActivities.map((activity) => {
              const previewText = activity.preview?.activityDescription || activity.content || t('exploreActivities.noDescription');
              const nodesCount = Array.isArray(activity.preview?.initialNodes) ? activity.preview.initialNodes.length : 0;
              return (
                <Card
                  key={activity.id}
                  onClick={() => onExploreActivityClick?.(activity)}
                  sx={{
                    cursor: 'pointer',
                    borderRadius: 3,
                    boxShadow: '0 12px 30px rgba(0,0,0,0.08)',
                    '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 16px 36px rgba(0,0,0,0.12)' },
                    transition: 'all 0.2s ease'
                  }}
                >
                  <CardContent>
                    <Stack spacing={1.5}>
                      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="h6" sx={{ fontWeight: 700, wordBreak: 'break-word' }}>
                            {activity.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                            {previewText}
                          </Typography>
                        </Box>
                        <Chip label={t('exploreActivities.public')} size="small" color="primary" variant="outlined" />
                      </Stack>
                      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                        <Chip label={t('exploreActivities.groupCount', { count: activity.groupCount || 0 })} size="small" />
                        <Chip label={t('exploreActivities.initialNodeCount', { count: nodesCount })} size="small" />
                        <Chip label={new Date(activity.createdAt || Date.now()).toLocaleDateString(i18n.language)} size="small" variant="outlined" />
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        </Box>
        )}

        <Grid container spacing={3} style={{ display: 'none' }}>
          <Grid item xs={12} md={6}>
            <AdminSupportCard onClick={handleAdminSupportClick} />
          </Grid>
        </Grid>

        <TemplateDetailDialog
          open={dialogOpen}
          onClose={handleDialogClose}
          template={selectedTemplate}
          onActivityCreated={onActivityCreated}
        />

        <GuidedTemplateWizard
          open={wizardOpen}
          onClose={handleCloseWizard}
          onActivityCreated={onActivityCreated}
          flowConfig={selectedFlow}
        />

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
}
